/* eslint-disable jsx-a11y/media-has-caption */
import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Dialog } from '@mui/material'
import classNames from 'classnames/bind'

import { ExpandIcon, PlayIcon, PlayedIcon, XMarkIcon } from '~/assets/icons'
import styles from '../styles/VideoDetail.module.css'
const cx = classNames.bind(styles)

const DEFAULT_VIDEO_SIZE = '390/488'

// Component hiển thị video tại màn hình chi tiết món ăn
const VideoDetail = ({ video_menu_url, video_size = DEFAULT_VIDEO_SIZE }) => {
  const refVideo = useRef()
  const refVideoFull = useRef()
  const refCurrentTimeVideo = useRef(0)
  const idTimer = useRef()
  const [isPlayed, setIsPlayed] = useState(true)
  const [isOverlayVisible, setOverlayVisible] = useState(false)
  const [isFullScreen, setIsFullScreen] = useState(false)
  const [widthVideo, heightVideo] = video_size
    ? video_size.split('/')
    : DEFAULT_VIDEO_SIZE.split('/')
  const isVerticalMode = Number(widthVideo) < Number(heightVideo) // kiểm tra ở chế độ dọc

  // Hàm xử lý play / pause video ở chế độ fullscreen
  const handleControlVideoFull = () => {
    if (!isPlayed) {
      refVideoFull.current?.play()
      setIsPlayed(true)
    } else {
      refVideoFull.current?.pause()
      setIsPlayed(false)
    }
  }

  // Hàm xử lý play / pause video ở chế độ thường
  const handleControlVideo = () => {
    if (!isPlayed) {
      refVideo.current?.play()
      setIsPlayed(true)
    } else {
      refVideo.current?.pause()
      setIsPlayed(false)
    }
  }

  // Hiển thị nút play/pause dùng chung
  const renderControlVideo = () => {
    return (
      <Box
        onClick={isFullScreen ? handleControlVideoFull : handleControlVideo}
        className={cx('btn_control', {
          fullscreen: isFullScreen,
          vertical_mode: isVerticalMode,
        })}>
        {isPlayed ? <PlayedIcon /> : <PlayIcon />}
      </Box>
    )
  }

  // Xử lý khi kết thúc video thì sẽ hiển thị nút play
  const handleEndedVideo = () => {
    setIsPlayed(false)
    handleVideoTouch()
    refCurrentTimeVideo.current = 0
  }

  // Xử lý khi chạm vào video thì sẽ hiển thị lớp phủ màu đen sau khoảng 1.5s thì tắt
  const handleVideoTouch = () => {
    setOverlayVisible(true)

    if (idTimer.current) clearTimeout(idTimer.current)
    idTimer.current = setTimeout(() => {
      setOverlayVisible(false)
    }, 1500)
  }

  // Open fullscreen
  const handleOpenFullScreen = () => {
    setIsFullScreen(true)
    refVideo.current?.pause()
    refVideoFull.current.currentTime = refCurrentTimeVideo.current
    if (isPlayed) refVideoFull.current?.play()
  }

  // Close fullscreen
  const handleCloseFullScreen = () => {
    setIsFullScreen(false)
    refVideoFull.current?.pause()
    refVideo.current.currentTime = refCurrentTimeVideo.current
    if (isPlayed) refVideo.current?.play()
  }

  // Xử lý update thời gian chạy hiện tại của video
  const handleTimeVideoUpdate = (e) => {
    refCurrentTimeVideo.current = e.target.currentTime
  }

  // Hiển thị nội dung của video trong trường hợp fullscreen
  const renderVideoContentFullScreen = () => {
    return (
      <>
        <video
          onTimeUpdate={handleTimeVideoUpdate}
          playsInline
          src={video_menu_url}
          className={cx(['video', 'fullscreen'], {
            vertical_mode: isVerticalMode,
          })}
          ref={refVideoFull}
          onTouchStart={handleVideoTouch}
          onEnded={handleEndedVideo}></video>
        {isOverlayVisible && <Box className={cx('video_overlay')}>{renderControlVideo()}</Box>}
      </>
    )
  }

  // Hiển thị nút đóng full màn hình
  const renderCloseFullScreen = () => {
    if (!isFullScreen) return null

    return (
      <Box
        bgcolor="rgba(255, 255, 255, 0.75)"
        onClick={handleCloseFullScreen}
        component="button"
        className={cx('close_btn')}>
        <XMarkIcon color="#000" size="12" />
      </Box>
    )
  }

  // Hiển thị popup full màn hình trong trường hợp bấm vào nút mở rộng
  const renderPopupFullVideo = () => {
    return (
      <Dialog
        style={{
          transform: isFullScreen ? 'translateY(0)' : 'translateY(200%)',
          transition: '0.3s',
          visibility: isFullScreen ? 'visible' : 'hidden',
        }}
        componentsProps={{ backdrop: { style: { backgroundColor: '#000' } } }}
        open={true}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          style: stylesPaper,
        }}>
        {renderCloseFullScreen()}
        {renderVideoContentFullScreen()}
      </Dialog>
    )
  }

  // Hiển thị nội dung video ở chế độ bình thường
  const renderVideoContent = () => {
    return (
      <video
        onTimeUpdate={handleTimeVideoUpdate}
        playsInline
        autoPlay
        src={video_menu_url}
        className={cx('video')}
        style={{
          aspectRatio: video_size,
        }}
        ref={refVideo}
        onTouchStart={handleVideoTouch}
        onEnded={handleEndedVideo}></video>
    )
  }

  // Hiển thị nút mở fullscreen
  const renderExpandIcon = () => {
    if (isFullScreen) return null

    return (
      <Box
        component="button"
        bgcolor="transparent"
        onClick={handleOpenFullScreen}
        position="absolute"
        top="20px"
        right="20px"
        zIndex={1}>
        <ExpandIcon />
      </Box>
    )
  }

  return (
    <>
      <Box className={cx('video_wrapper')}>
        {renderVideoContent()}
        {isOverlayVisible && <Box className={cx('video_overlay')}>{renderControlVideo()}</Box>}
        {renderExpandIcon()}
      </Box>
      {renderPopupFullVideo()}
    </>
  )
}

VideoDetail.propTypes = {
  video_menu_url: PropTypes.string,
  video_size: PropTypes.string,
}

export default React.memo(VideoDetail)

const stylesPaper = {
  width: '100%',
  margin: 0,
  height: 'calc(100vw * 16 / 9)',
  maxHeight: 'calc(100vw * 16 / 9)',
  borderRadius: 0,
  backgroundColor: '#000',
  position: 'relative',
  overflow: 'hidden',
}
